import { useState } from "react";
import { ArrowLeft, Grid3X3, CreditCard, ShoppingBag, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TableNicknameDialog } from "@/components/tables/TableNicknameDialog";
import type { SelectedDestination } from "./PDVSelectionScreen";
import { cn } from "@/lib/utils";
import { useConnectivity } from "@/hooks/useConnectivity";

interface PDVOrderHeaderProps {
  destination: SelectedDestination;
  onBack: () => void;
  quantity?: number;
  onQuantityChange?: (qty: number) => void;
  tableName?: string | null;
  onUpdateTableName?: (name: string | null) => Promise<void>;
  commandName?: string | null;
  onUpdateCommandName?: (name: string | null) => Promise<void>;
}

export function PDVOrderHeader({
  destination,
  onBack,
  quantity = 1,
  onQuantityChange,
  tableName,
  onUpdateTableName,
  commandName,
  onUpdateCommandName,
}: PDVOrderHeaderProps) {
  const [nicknameDialogOpen, setNicknameDialogOpen] = useState(false);
  const [commandNicknameDialogOpen, setCommandNicknameDialogOpen] = useState(false);
  const { isOnline: isConnOnline } = useConnectivity();

  const getDestinationIcon = () => {
    switch (destination.type) {
      case "table":
        return <Grid3X3 className="h-5 w-5" />;
      case "tab":
        return <CreditCard className="h-5 w-5" />;
      case "counter":
        return <ShoppingBag className="h-5 w-5" />;
    }
  };

  const getDestinationLabel = () => {
    switch (destination.type) {
      case "table":
        return `Mesa ${destination.number}`;
      case "tab":
        return `Comanda ${destination.number}`;
      case "counter":
        return "Balcão";
    }
  };

  return (
    <>
    <div className="h-12 bg-[var(--pdv-surface-elevated)] border-b border-[var(--pdv-border)] px-4 flex items-center shrink-0">
        <div className="flex items-center gap-3 w-full">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="shrink-0 h-9 w-9 text-[var(--pdv-text-muted)] hover:text-[var(--pdv-text)] hover:bg-[var(--pdv-highlight-hover)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          {/* Brand */}
          <span className="font-['Exo_2'] font-bold text-lg text-[var(--pdv-accent)] tracking-tight select-none hidden sm:inline">
            ALTUS<span className="text-[var(--pdv-text)]"> Food</span>
          </span>

          {/* Destination Badge */}
          <Badge 
            variant="secondary" 
            className="h-8 px-3 text-sm font-semibold gap-2 bg-[var(--pdv-highlight)] text-[var(--pdv-accent-light)] border border-[var(--pdv-highlight-hover-border)] rounded-full"
          >
            {getDestinationIcon()}
            {getDestinationLabel()}
          </Badge>
          
          {/* Table name / nickname */}
          {destination.type === "table" && (
            <div className="flex items-center gap-1">
              {tableName && (
                <span className="text-sm text-[var(--pdv-text-muted)]">
                  {tableName}
                </span>
              )}
              {onUpdateTableName && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-[var(--pdv-text-subtle)] hover:text-[var(--pdv-text)]"
                  onClick={() => setNicknameDialogOpen(true)}
                  title="Editar apelido da mesa"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
          
          {/* Command name / nickname */}
          {destination.type === "tab" && (
            <div className="flex items-center gap-1">
              {(commandName || destination.customerName) && (
                <span className="text-sm text-[var(--pdv-text-muted)]">
                  {commandName || destination.customerName}
                </span>
              )}
              {onUpdateCommandName && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-[var(--pdv-text-subtle)] hover:text-[var(--pdv-text)]"
                  onClick={() => setCommandNicknameDialogOpen(true)}
                  title="Editar apelido da comanda"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Online status */}
          <div className="hidden md:flex items-center gap-1.5 text-xs text-[var(--pdv-text-subtle)]">
            <div
              className={cn(
                "w-2 h-2 rounded-full animate-pulse",
                isConnOnline ? "bg-[var(--pdv-success)]" : "bg-destructive",
              )}
            />
            {isConnOnline ? "Online" : "Offline"}
          </div>

          {/* Quantity selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--pdv-text-subtle)]">Qtd:</span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 bg-[var(--pdv-surface)] border-[var(--pdv-border)] text-[var(--pdv-text)] hover:bg-[var(--pdv-highlight-hover)]"
                onClick={() => onQuantityChange?.(Math.max(1, quantity - 1))}
              >
                -
              </Button>
              <span className="w-8 text-center font-semibold text-[var(--pdv-text)]">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 bg-[var(--pdv-surface)] border-[var(--pdv-border)] text-[var(--pdv-text)] hover:bg-[var(--pdv-highlight-hover)]"
                onClick={() => onQuantityChange?.(quantity + 1)}
              >
                +
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Nickname dialog for tables */}
      {destination.type === "table" && onUpdateTableName && (
        <TableNicknameDialog
          open={nicknameDialogOpen}
          onOpenChange={setNicknameDialogOpen}
          tableNumber={destination.number}
          currentName={tableName || null}
          onSave={onUpdateTableName}
        />
      )}

      {/* Nickname dialog for commands */}
      {destination.type === "tab" && onUpdateCommandName && (
        <TableNicknameDialog
          open={commandNicknameDialogOpen}
          onOpenChange={setCommandNicknameDialogOpen}
          tableNumber={destination.number}
          currentName={commandName || null}
          onSave={onUpdateCommandName}
          isCommand
        />
      )}
    </>
  );
}
